import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cloud, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const ENGAGEMENT_KEY = "wwi_engagement";
const INVITATION_DISMISSED_KEY = "wwi_sync_invitation_dismissed_until"; // ISO timestamp string
const HAS_SIGNED_IN_KEY = "wwi_has_signed_in"; // sticky flag — once true, never bug again
const SESSION_TOKEN_KEY = "wwi_session_token";

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

// Defensive check: only show the "Save my thread" prompt when we are
// reasonably sure the user is NOT logged in. A stored session token is
// treated as a confirmed-ish login even if /auth/check hasn't resolved yet.
const hasStoredSession = () => {
  try {
    return !!localStorage.getItem(SESSION_TOKEN_KEY);
  } catch (e) {
    return false;
  }
};

// Has the user ever successfully signed in on this browser? If yes, never show this.
const hasEverSignedIn = () => {
  try {
    return localStorage.getItem(HAS_SIGNED_IN_KEY) === "true";
  } catch (e) {
    return false;
  }
};

// Is the popup currently in its "snoozed" window (7 days after last dismissal)?
const isCurrentlySnoozed = () => {
  try {
    const until = localStorage.getItem(INVITATION_DISMISSED_KEY);
    if (!until) return false;
    // Legacy value: previously stored 'true' for permanent dismiss — honor it.
    if (until === "true") return true;
    const untilTs = parseInt(until, 10);
    if (Number.isNaN(untilTs)) return false;
    return Date.now() < untilTs;
  } catch (e) {
    return false;
  }
};

// Track user engagement
export const trackEngagement = () => {
  const today = new Date().toISOString().split('T')[0];
  const stored = localStorage.getItem(ENGAGEMENT_KEY);
  let engagement = stored ? JSON.parse(stored) : { 
    taskCount: 0, 
    daysUsed: [], 
    firstUse: today 
  };
  
  // Add today to days used if not already there
  if (!engagement.daysUsed.includes(today)) {
    engagement.daysUsed.push(today);
  }
  
  localStorage.setItem(ENGAGEMENT_KEY, JSON.stringify(engagement));
  return engagement;
};

// Increment task count
export const incrementTaskCount = () => {
  const stored = localStorage.getItem(ENGAGEMENT_KEY);
  let engagement = stored ? JSON.parse(stored) : { 
    taskCount: 0, 
    daysUsed: [], 
    firstUse: new Date().toISOString().split('T')[0]
  };
  
  engagement.taskCount = (engagement.taskCount || 0) + 1;
  localStorage.setItem(ENGAGEMENT_KEY, JSON.stringify(engagement));
  return engagement;
};

// Check if we should show the invitation
const shouldShowInvitation = () => {
  // Hard stops: user has signed in before, or is currently snoozed
  if (hasEverSignedIn()) return false;
  if (isCurrentlySnoozed()) return false;

  const stored = localStorage.getItem(ENGAGEMENT_KEY);
  if (!stored) return false;
  
  const engagement = JSON.parse(stored);
  
  // Show if: 3+ tasks created OR 2+ days of use
  const hasMultipleTasks = (engagement.taskCount || 0) >= 3;
  const hasMultipleDays = (engagement.daysUsed || []).length >= 2;
  
  return hasMultipleTasks || hasMultipleDays;
};

export const SyncInvitation = () => {
  const [isVisible, setIsVisible] = useState(false);
  const { isAuthenticated, loading, signIn } = useAuth();

  // Sticky flag: once authenticated, never show this popup again on this browser
  useEffect(() => {
    if (isAuthenticated) {
      try {
        localStorage.setItem(HAS_SIGNED_IN_KEY, "true");
      } catch (e) { /* ignore */ }
    }
  }, [isAuthenticated]);

  useEffect(() => {
    // Don't show while auth is still loading, if already authenticated,
    // if a session token exists, if user has ever signed in, or if snoozed
    if (loading || isAuthenticated || hasStoredSession() || hasEverSignedIn() || isCurrentlySnoozed()) {
      setIsVisible(false);
      return;
    }

    // Track engagement and check if we should show
    trackEngagement();
    
    // Delay showing to not interrupt immediate flow
    const timer = setTimeout(() => {
      // Re-check auth signals right before showing (auth may have resolved)
      if (hasStoredSession() || hasEverSignedIn() || isCurrentlySnoozed()) return;
      if (shouldShowInvitation()) {
        setIsVisible(true);
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, [isAuthenticated, loading]);

  const handleDismiss = () => {
    setIsVisible(false);
    // Snooze for 7 days regardless of which button was used
    try {
      const until = Date.now() + SEVEN_DAYS_MS;
      localStorage.setItem(INVITATION_DISMISSED_KEY, String(until));
    } catch (e) { /* ignore */ }
  };

  const handleSignIn = () => {
    setIsVisible(false);
    signIn();
  };

  if (!isVisible || isAuthenticated || loading || hasStoredSession() || hasEverSignedIn() || isCurrentlySnoozed()) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 10, scale: 0.95 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="fixed bottom-24 right-4 z-40 w-full max-w-[280px] scale-[0.7] origin-bottom-right"
        data-testid="sync-invitation"
      >
        <div className="bg-white rounded-2xl shadow-lg border border-stone-100 p-5 relative">
          {/* Close button - positioned outside the card area */}
          <button
            onClick={handleDismiss}
            className="absolute -top-2 -right-2 p-1.5 bg-white rounded-full shadow-md text-stone-400 hover:text-stone-600 hover:bg-stone-50 transition-colors border border-stone-100"
            aria-label="Dismiss"
          >
            <X size={14} />
          </button>

          {/* Content */}
          <div className="flex items-start gap-4">
            <div className="p-2.5 bg-[#E0F7FA] rounded-xl">
              <Cloud size={22} className="text-[#3DC4D0]" />
            </div>
            
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-[#27272A] text-sm mb-1">
                Want me to remember this?
              </h3>
              <p className="text-xs text-[#52525B] mb-4">
                Keep your threads safe across devices. No passwords needed.
              </p>

              <div className="flex gap-2">
                <button
                  onClick={handleSignIn}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-[#3DC4D0] text-white rounded-xl text-sm font-medium hover:bg-[#2AA8AB] transition-colors"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Save my thread
                </button>
                <button
                  onClick={handleDismiss}
                  className="px-4 py-2.5 text-stone-500 hover:text-stone-700 text-sm transition-colors"
                >
                  Not now
                </button>
              </div>
            </div>
          </div>

          {/* Trust note */}
          <p className="mt-4 pt-3 border-t border-stone-100 text-[10px] text-[#A1A1AA] text-center">
            Your data stays yours. No ads, no tracking, no marketing emails.
          </p>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SyncInvitation;
